import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Users, AlertCircle, Play } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { LanguageSelector } from '../components/LanguageSelector';
import { Player } from '../types';

interface ImpostorSelectionPageProps {
  players: Player[];
  currentPlayer: Player;
  selectedCategory: string;
  impostorCount?: number;
  onBack: () => void;
  onSelectImpostorCount: (count: number) => void;
  onStartGame: () => void;
}

export const ImpostorSelectionPage: React.FC<ImpostorSelectionPageProps> = ({
  players,
  currentPlayer,
  selectedCategory,
  impostorCount,
  onBack,
  onSelectImpostorCount,
  onStartGame,
}) => {
  const { t } = useTranslation();
  const [selectedCount, setSelectedCount] = useState(impostorCount || 1);

  const isHost = currentPlayer.isHost;
  const playerCount = players.filter(p => p.isConnected).length;
  
  // Reglas: 3 jugadores = 1 impostor, 4-10 jugadores = 1-2 impostores
  const maxImpostors = playerCount === 3 ? 1 : 2;
  const canStartGame = impostorCount != null;  // Acepta null o undefined

  // Debug logs
  console.log('ImpostorSelectionPage - currentPlayer:', currentPlayer);
  console.log('ImpostorSelectionPage - isHost:', isHost);
  console.log('ImpostorSelectionPage - impostorCount:', impostorCount);
  console.log('ImpostorSelectionPage - playerCount:', playerCount);
  console.log('ImpostorSelectionPage - maxImpostors:', maxImpostors);
  console.log('ImpostorSelectionPage - canStartGame:', canStartGame);

  const handleConfirm = () => {
    onSelectImpostorCount(selectedCount);
  };

  const handleStartGame = () => {
    onStartGame();
  };

  const getCategoryEmoji = (category: string) => {
    const emojis: Record<string, string> = {
      animales: '🐾',
      comida: '🍔',
      objetos: '📦',
      películas: '🎬',
      lugares: '🌍',
      profesiones: '💼',
    };
    return emojis[category] || '📋';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20 pointer-events-none"></div>
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          Volver
        </Button>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            ⚙️ Configuración del Juego
          </h1>
        </div>

        {/* Category Selected */}
        <Card className="mb-6 border-2 border-success-400 bg-success-500/10">
          <div className="text-center">
            <div className="text-6xl mb-3">{getCategoryEmoji(selectedCategory)}</div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Categoría seleccionada
            </h2>
            <p className="text-xl text-white/80 capitalize">
              {selectedCategory}
            </p>
          </div>
        </Card>

        {/* Players Count */}
        <Card className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6 text-white" />
            <h2 className="text-2xl font-bold text-white">
              Jugadores: {playerCount}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {players.filter(p => p.isConnected).map((player) => (
              <div
                key={player.id}
                className="p-3 rounded-lg bg-white/10 border border-white/20"
              >
                <div className="text-white font-medium truncate">
                  {player.name}
                  {player.isHost && (
                    <span className="ml-2 text-xs bg-primary-500 px-2 py-1 rounded">
                      Anfitrión
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Impostor Selection (Only Host) */}
        {isHost && impostorCount == null ? (
          <Card className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-4">
              Selecciona la cantidad de impostores
            </h2>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              {[1, 2].map((count) => (
                <button
                  key={count}
                  onClick={() => setSelectedCount(count)}
                  disabled={count > maxImpostors}
                  className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                    count > maxImpostors
                      ? 'border-white/10 bg-white/5 opacity-30 cursor-not-allowed'
                      : selectedCount === count
                      ? 'border-primary-500 bg-primary-500/20 scale-105'
                      : 'border-white/30 bg-white/10 hover:border-primary-400 hover:bg-primary-500/10'
                  }`}
                >
                  <div className="text-4xl mb-2">
                    {count === 1 ? '😈' : '😈😈'}
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {count} {count === 1 ? 'Impostor' : 'Impostores'}
                  </div>
                </button>
              ))}
            </div>

            {playerCount === 3 && (
              <div className="flex items-start gap-3 p-4 bg-accent-500/10 border border-accent-500/30 rounded-lg mb-4">
                <AlertCircle className="w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm">
                  Con 3 jugadores, solo puedes tener 1 impostor
                </p>
              </div>
            )}

            <div className="flex justify-center mt-6">
              <Button
                variant="primary"
                size="lg"
                onClick={handleConfirm}
                icon={<Users className="w-5 h-5" />}
              >
                Confirmar Impostores
              </Button>
            </div>
          </Card>
        ) : !isHost && impostorCount == null ? (
          <Card className="text-center mb-6">
            <p className="text-white/80">
              Esperando a que el anfitrión configure los impostores...
            </p>
          </Card>
        ) : null}

        {/* Waiting or Start Game */}
        {impostorCount != null && (
          <Card className="mb-6 border-2 border-success-400 bg-success-500/10">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">
                {impostorCount === 1 ? '😈' : '😈😈'}
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {impostorCount} {impostorCount === 1 ? 'Impostor' : 'Impostores'}
              </h2>
              <p className="text-white/80">
                {isHost ? 'Todo listo. Inicia el juego cuando estés listo.' : 'Esperando que el anfitrión inicie el juego...'}
              </p>
            </div>

            {isHost && (
              <div className="flex justify-center">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleStartGame}
                  icon={<Play className="w-5 h-5" />}
                >
                  Iniciar Juego
                </Button>
              </div>
            )}
          </Card>
        )}

        {!isHost && impostorCount == null && (
          <Card className="text-center">
            <p className="text-white/80">
              Esperando a que el anfitrión configure el juego...
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};
